<style lang="scss" scoped>
/* 全链路日志追踪抽屉：左侧关联脑图（DOM 树，编号可选中复制） + 右侧节点日志列表 */
.log-chain {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0 16px 16px;
}

.log-chain__bar {
    flex: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
}

.log-chain__input {
    width: 320px;
    max-width: 100%;
}

.log-chain__resolved {
    color: #67c23a;
    font-size: 13px;
    white-space: nowrap;
}

.log-chain__empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
}

.log-chain__body {
    flex: 1;
    display: flex;
    gap: 10px;
    min-height: 0;
}

.log-chain__chartwrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fafcff;
    overflow: hidden;
}

.log-chain__legend {
    flex: none;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    padding: 8px 12px 0;
    font-size: 12px;
    color: #606266;

    i {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 4px;
    }
}

.log-chain__tree {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px;
}

.log-chain__panel {
    flex: 0 0 580px;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.log-chain__head {
    flex: none;
    font-weight: bold;
    margin-bottom: 8px;
    overflow-wrap: anywhere;
}

/* 节点详情：点击节点加载的档案/记录信息，保险绑定表放下方 */
.log-chain__detail {
    flex: none;
    max-height: 46%;
    overflow: auto;
    padding: 8px 12px;
    margin-bottom: 8px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fafcff;
}

.log-chain__grid {
    display: grid;
    grid-template-columns: 88px minmax(0, 1fr) 88px minmax(0, 1fr);
    gap: 4px 10px;
    margin: 0;

    dt {
        color: #606266;
    }

    dd {
        margin: 0;
        overflow-wrap: anywhere;
    }
}

.log-chain__detail-sub {
    margin: 8px 0 6px;
    font-weight: bold;
    color: #303133;
}

.log-chain__filter {
    flex: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;

    .el-date-editor {
        max-width: 320px;
    }
}

.log-chain__table {
    flex: 1;
    min-height: 0;
    overflow: auto;

    .log-detail {
        margin: 0;
    }
}

.log-chain-pagination {
    flex: none;
    margin-top: 10px;
    overflow-x: auto;
}

.log-detail {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
</style>

<style lang="scss">
/* append-to-body 抽屉脱离组件作用域：头部定高、内容区绝对定位铺满（log-chain- 前缀防污染） */
.el-drawer__wrapper .log-chain-drawer {
    .el-drawer__header {
        height: 46px;
        box-sizing: border-box;
        padding: 12px 16px;
        margin-bottom: 0;
        font-weight: bold;
        color: #303133;
    }

    .el-drawer__body {
        position: absolute;
        top: 46px;
        right: 0;
        bottom: 0;
        left: 0;
        overflow: hidden;
    }
}
</style>

<template>
    <el-drawer title="全链路日志追踪" :visible.sync="visible" direction="rtl" size="100%" append-to-body custom-class="log-chain-drawer" :wrapper-closable="false">
        <div class="log-chain">
            <!-- 追踪入口：输入车辆号/中控号/车架号自动识别身份；多身份命中时给候选切换 -->
            <div class="log-chain__bar">
                <el-input v-model.trim="key" class="log-chain__input" size="medium" clearable placeholder="输入车辆号 / 中控号 / 车架号" @keyup.enter.native="search()" @clear="clearChain" />
                <el-button type="primary" size="medium" :loading="resolving || expanding" @click="search()"><i class="fa fa-location-arrow"></i> 追踪</el-button>
                <el-radio-group v-if="candidates.length" v-model="candidateType" size="small" @change="pickCandidate">
                    <el-radio-button v-for="item in candidates" :key="item.keyType" :label="item.keyType">{{ keyTypeText(item.keyType) }}（{{ item.summary }}）</el-radio-button>
                </el-radio-group>
                <span v-if="resolvedText" class="log-chain__resolved">{{ resolvedText }}</span>
            </div>
            <div v-if="!root" class="log-chain__empty">{{ emptyText }}</div>
            <div v-else class="log-chain__body">
                <!-- 左侧：关联脑图（纯结构，日志点卡片在右侧看；编号为真实 DOM 文本可选中复制） -->
                <div class="log-chain__chartwrap">
                    <div class="log-chain__legend">
                        <span v-for="item in legendItems" :key="item.name"><i :style="{ background: item.color }" />{{ item.name }}</span>
                    </div>
                    <div v-loading="expanding" :element-loading-text="`全链展开中…已加载 ${expandProgress} 个节点`" class="log-chain__tree">
                        <log-chain-node :node="treeData" :is-root="true" :selected-path="selectedPath" @select="onNodeSelect" @expand="onNodeExpand" />
                    </div>
                </div>
                <div class="log-chain__panel">
                    <div class="log-chain__head">{{ selected ? `${selected.typeName}：${selected.businessKey}${selected.platform ? `（${selected.platform}）` : ''}` : '点击节点查看详情与日志；点 ▸ 展开/收起关联（中控按日志追历史绑定车架），编号可选中复制' }}</div>
                    <!-- 节点详情：车辆同步取该平台最新一条记录；车辆/中控/车架节点下方附当前生效保险，保险节点列全部绑定（开始~结束为绑定期间） -->
                    <div v-if="selected" v-loading="detailLoading" class="log-chain__detail">
                        <template v-if="detail">
                            <dl v-if="detailFieldList.length" class="log-chain__grid">
                                <template v-for="field in detailFieldList"><dt :key="field.key + '-label'">{{ field.label }}</dt><dd :key="field.key">{{ formatDetail(field) }}</dd></template>
                            </dl>
                            <template v-if="detail.insurances && detail.insurances.length">
                                <div class="log-chain__detail-sub">{{ selected.nodeType === 'insurance' ? `保险绑定（共 ${detail.insurances.length} 条）` : '当前保险' }}</div>
                                <el-table :data="detail.insurances" size="mini" border>
                                    <el-table-column prop="policyNumber" label="保单号" min-width="150" show-overflow-tooltip />
                                    <el-table-column prop="company" label="保险公司" min-width="100" show-overflow-tooltip />
                                    <el-table-column prop="insuranceArea" label="保险区域" min-width="80" show-overflow-tooltip />
                                    <el-table-column label="绑定开始" width="92"><template slot-scope="scope">{{ date(scope.row.startDate) }}</template></el-table-column>
                                    <el-table-column label="绑定结束" width="92"><template slot-scope="scope">{{ scope.row.endDate ? date(scope.row.endDate) : '开放结束' }}</template></el-table-column>
                                    <el-table-column label="状态" width="72"><template slot-scope="scope"><el-tag size="mini" :type="scope.row.validTag === '有效' ? 'success' : scope.row.validTag === '未生效' ? 'warning' : 'info'">{{ scope.row.validTag || '-' }}</el-tag></template></el-table-column>
                                </el-table>
                            </template>
                        </template>
                    </div>
                    <div class="log-chain__filter">
                        <el-date-picker v-model="selectedTime" type="datetimerange" size="small" :picker-options="pickerOptions" :default-time="['00:00:00', '23:59:59']" range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" align="right" @change="searchLogs" />
                        <el-select v-model="filters.action" size="small" clearable placeholder="动作" style="width: 100px" @change="actionChanged">
                            <el-option v-for="item in arrVehicleDataChangeAction" :key="item.value" :label="item.desc" :value="item.value" />
                        </el-select>
                        <el-input v-model.trim="filters.keywords" size="small" clearable placeholder="关键字" style="width: 130px" @clear="searchLogs" @keyup.enter.native="searchLogs" />
                        <el-button size="small" type="primary" @click="searchLogs"><i class="fa fa-search"></i> 查询</el-button>
                    </div>
                    <div class="log-chain__table">
                        <el-table v-loading="loading" :data="rows" style="width: 100%" border size="small" height="100%">
                            <el-table-column type="expand">
                                <template slot-scope="scope">
                                    <el-table v-if="Array.isArray(details(scope.row.detail))" :data="details(scope.row.detail)" size="mini" border>
                                        <el-table-column prop="field" label="字段" width="160" />
                                        <el-table-column label="修改前"><template slot-scope="diff">{{ display(diff.row.old) }}</template></el-table-column>
                                        <el-table-column label="修改后"><template slot-scope="diff">{{ display(diff.row.new) }}</template></el-table-column>
                                    </el-table>
                                    <pre v-else class="log-detail">{{ display(details(scope.row.detail)) }}</pre>
                                </template>
                            </el-table-column>
                            <el-table-column label="时间" width="150"><template slot-scope="scope">{{ dateTime(scope.row.creationTime) }}</template></el-table-column>
                            <el-table-column label="动作" width="80"><template slot-scope="scope">{{ actionName(scope.row.action) }}</template></el-table-column>
                            <el-table-column label="来源" width="60"><template slot-scope="scope">{{ sourceName(scope.row.source) }}</template></el-table-column>
                            <el-table-column label="操作人" width="90"><template slot-scope="scope">{{ scope.row.operatorName || '-' }}</template></el-table-column>
                            <el-table-column label="关联键" min-width="120"><template slot-scope="scope">{{ relateKeys(scope.row) }}</template></el-table-column>
                        </el-table>
                    </div>
                    <el-pagination class="log-chain-pagination" background :current-page.sync="page" :page-size="size" :total="total" layout="total, prev, pager, next" @current-change="loadLogs" />
                </div>
            </div>
        </div>
    </el-drawer>
</template>

<script>
    // 全链路日志追踪：输入编号后端建链（车辆同步→中控→车架→车架保险→保单），脑图只画关联关系，
    // 点卡片在右侧查该节点日志；节点渲染用递归 DOM 组件 LogChainNode（编号可选中复制），
    // 历史(+N) 节点点击在图内展开/收起沿革（数据一次到位，前端按路径裁剪）
    import moment from 'moment'
    import { vehicleDataApi } from '@/api/vehicleData'
    import enumConfigs from '@/mixins/enumConfigs'
    import LogChainNode, { nodeColors } from './LogChainNode'

    const nodeTypeNames = { vehicle: '车辆同步', control: '中控', chassis: '车架', insurance: '车架保险', policy: '保单' }
    const keyTypeNames = { 1: '车辆号', 2: '中控号', 3: '车架号' }

    // 各节点类型的详情字段（车辆同步=该平台最新一条记录；中控/车架/保单=档案字段；保险节点只显示绑定表）
    const detailFieldSets = {
        vehicle: [
            { key: 'area', label: '服务区' },
            { key: 'controlNo', label: '中控号' },
            { key: 'status', label: '车辆状态' },
            { key: 'lastSyncTime', label: '最新同步时间', time: true },
            { key: 'lastOrderTime', label: '最后订单时间', time: true },
            { key: 'lastOnlineTime', label: '最后定位时间', time: true }
        ],
        control: [
            { key: 'controlSN', label: '中控 SN' },
            { key: 'model', label: '型号' },
            { key: 'chassisNo', label: '当前车架' },
            { key: 'deviceExpiryDate', label: '设备到期日期', date: true }
        ],
        chassis: [
            { key: 'model', label: '型号' },
            { key: 'motorNo', label: '电机号' }
        ],
        insurance: [],
        policy: [
            { key: 'policyType', label: '保单类型' },
            { key: 'company', label: '保险公司' },
            { key: 'policyHolder', label: '投保人' },
            { key: 'insuredName', label: '被保险人' },
            { key: 'policyStartDate', label: '保单开始', date: true },
            { key: 'policyEndDate', label: '保单结束', date: true },
            { key: 'remark', label: '备注' }
        ]
    }

    export default {
        name: 'LogChainDrawer',
        components: { LogChainNode },
        mixins: [enumConfigs],
        data() {
            return {
                visible: false, resolving: false, loading: false, historyArmed: false,
                key: '', result: null, candidateType: null,
                // open(key, autoExpand) 决定建链后是否自动展开全链（默认 false 逐级手动）；expanding/expandProgress 为自动展开进度，searchSeq 使进行中的展开随新查询/清空/关抽屉失效
                autoExpand: false, expanding: false, expandProgress: 0, searchSeq: 0,
                root: null, expandedPaths: [], childrenCache: {}, appeared: {}, selectedPath: null, selected: null,
                detail: null, detailLoading: false,
                selectedTime: [], filters: { action: null, keywords: '' },
                rows: [], total: 0, page: 1, size: 20,
                legendItems: [
                    { name: '车辆同步', color: nodeColors.vehicle },
                    { name: '中控', color: nodeColors.control },
                    { name: '车架', color: nodeColors.chassis },
                    { name: '车架保险', color: nodeColors.insurance },
                    { name: '保单', color: nodeColors.policy },
                    { name: '历史/缺口', color: nodeColors.history }
                ],
                emptyText: '输入车辆号、中控号或车架号开始追踪'
            }
        },
        watch: {
            // 浏览器返回键关闭抽屉：打开时推入一条历史记录（保留 vue-router 自身 state），
            // 返回键只关抽屉不离开页面；用叉/ESC 关闭时再 history.back() 消费掉该记录
            visible(val) {
                if (val) {
                    this.historyArmed = true
                    window.history.pushState({ ...(window.history.state || {}), flcxLogChain: true }, '')
                    window.addEventListener('popstate', this.onDrawerPopState)
                } else {
                    window.removeEventListener('popstate', this.onDrawerPopState)
                    this.searchSeq++
                    this.expanding = false
                    if (this.historyArmed) {
                        this.historyArmed = false
                        window.history.back()
                    }
                }
            }
        },
        beforeDestroy() {
            window.removeEventListener('popstate', this.onDrawerPopState)
        },
        computed: {
            candidates() { return (this.result && this.result.candidates) || [] },
            // 当前节点类型的详情字段集
            detailFieldList() { return this.detail ? (detailFieldSets[this.detail.nodeType] || []) : [] },
            resolvedText() {
                if (!this.result || !this.result.keyType || !this.root) return ''
                return `已识别为${this.keyTypeText(this.result.keyType)}`
            },
            // 渲染树：根 + 懒加载缓存按展开状态拼装，路径用于选中高亮与展开记忆
            treeData() { return this.root ? this.buildTreeData(this.root, '0') : null },
            // 时间范围快捷选项（与变更日志弹窗一致：起始往前推，截止到昨天）
            pickerOptions() {
                const shortcut = (text, spaceTime, unit) => ({
                    text,
                    onClick: picker => picker.$emit('pick', [
                        moment().subtract(spaceTime, unit).format('YYYY-MM-DD 00:00:00'),
                        moment().subtract(1, 'd').format('YYYY-MM-DD 23:59:59')
                    ])
                })
                return {
                    shortcuts: [
                        shortcut('昨天', 1, 'd'),
                        shortcut('近一周', 1, 'w'),
                        shortcut('近一月', 1, 'M'),
                        shortcut('近三月', 3, 'M'),
                        shortcut('近半年', 6, 'M'),
                        shortcut('近一年', 1, 'y')
                    ]
                }
            }
        },
        methods: {
            keyTypeText(value) { return keyTypeNames[value] || '编号' },
            // 浏览器返回键：关抽屉（历史记录已由浏览器消费，不再后退离开页面）
            onDrawerPopState() {
                this.historyArmed = false
                if (this.visible) this.visible = false
            },
            // 打开抽屉：带编号则填入并立即追踪（第二个参数传 true 时建链后自动展开全链，默认逐级手动），否则空链开始由用户输入
            open(key, autoExpand = false) {
                this.clearChain()
                this.result = null
                this.key = key || ''
                this.autoExpand = !!autoExpand
                this.visible = true
                if (this.key) this.search()
            },
            clearChain() {
                this.searchSeq++
                this.expanding = false
                this.expandProgress = 0
                this.root = null
                this.selected = null
                this.selectedPath = null
                this.detail = null
                this.detailLoading = false
                this.candidateType = null
                this.expandedPaths = []
                this.childrenCache = {}
                this.appeared = {}
                this.rows = []
                this.total = 0
                this.page = 1
                this.selectedTime = []
                this.filters = { action: null, keywords: '' }
                this.emptyText = '输入车辆号、中控号或车架号开始追踪'
            },
            async search(keyType) {
                if (!this.key) {
                    this.$message.warning('请输入车辆号、中控号或车架号')
                    return
                }
                this.resolving = true
                this.searchSeq++
                try {
                    const params = { key: this.key }
                    const picked = keyType || this.candidateType
                    if (picked) params.keyType = picked
                    const result = await vehicleDataApi.logChain(params)
                    this.result = result
                    this.expandedPaths = []
                    this.childrenCache = {}
                    this.appeared = {}
                    if (!result.root) {
                        this.root = null
                        this.selected = null
                        this.rows = []
                        this.total = 0
                        this.emptyText = (result.candidates || []).length
                            ? '该编号对应多种身份，请选择上方身份继续'
                            : `未找到「${this.key}」对应的车辆号、中控号或车架号`
                        return
                    }
                    this.root = result.root
                    this.registerNode(this.root, '0')
                    // 根节点展开：autoExpand（open 第二参传 true）时建链后按层自动展开全链，否则只展开第一层（如多平台车辆先分叉出各平台数据）
                    if (this.autoExpand) await this.expandAll()
                    else await this.onNodeExpand(this.treeData)
                } finally {
                    this.resolving = false
                }
            },
            // 候选身份选择后按该身份重建链路
            async pickCandidate() {
                if (this.candidateType != null) await this.search(this.candidateType)
            },
            // 渲染树节点：全图按身份去重——登记所有出现过的节点，同身份只显示首次出现，其余隐藏不渲染
            // （保单卡豁免：一张卡是一条车架↔保单绑定，同保单可绑多个车架，按保单号去重会吞卡）
            buildTreeData(node, path) {
                const expanded = this.expandedPaths.includes(path)
                const cache = this.childrenCache[path]
                const identity = this.nodeIdentity(node)
                const hidden = !!identity && !!this.appeared[identity] && this.appeared[identity] !== path
                const built = expanded && cache
                    ? cache.nodes.map((child, index) => this.buildTreeData(child, `${path}-${index}`))
                    : []
                return {
                    path,
                    nodeType: node.nodeType,
                    entityType: node.entityType,
                    businessKey: node.businessKey,
                    label: node.label,
                    subLabel: node.subLabel || '',
                    platform: node.platform || '',
                    keyType: node.keyType,
                    logCount: node.logCount || 0,
                    expandable: !!node.expandable,
                    validTag: node.validTag || '',
                    hidden,
                    allHidden: built.length > 0 && built.every(child => child.hidden),
                    expanded,
                    loading: !!(expanded && cache && cache.loading),
                    children: built.filter(child => !child.hidden)
                }
            },
            // 节点身份键：同身份全图只显示一次；车辆按 平台|车辆号 成对；
            // 保单卡不去重（保单是叶子不成环，且一张卡=一条绑定，同保单绑多车架时每个车架下都要显示）
            nodeIdentity(node) {
                const key = node.businessKey || ''
                if (node.nodeType === 'vehicle') return `v:${node.platform || ''}|${key}`
                if (node.nodeType === 'control') return `c:${key}`
                if (node.nodeType === 'chassis') return `h:${key}`
                if (node.nodeType === 'insurance') return `i:${key}`
                return null
            },
            // 登记节点身份（首个出现的路径为显示位）
            registerNode(node, path) {
                const identity = this.nodeIdentity(node)
                if (identity && !this.appeared[identity]) this.appeared[identity] = path
            },
            // 展开加载子节点时逐个登记
            registerChildren(node) {
                const cache = this.childrenCache[node.path]
                if (!cache) return
                cache.nodes.forEach((child, index) => this.registerNode(child, `${node.path}-${index}`))
            },
            // 节点点击：占位/查询包装节点忽略，实体节点选中并在右侧查日志
            onNodeSelect(node) {
                if (node.nodeType === 'missing' || node.nodeType === 'query') return
                if (node.path === this.selectedPath) return
                this.selectNode(node)
            },
            // 展开按钮：收起/展开切换；首次展开调接口懒加载下一层关联（以日志追历史绑定）
            async onNodeExpand(node) {
                const path = node.path
                if (this.expandedPaths.includes(path)) {
                    this.expandedPaths.splice(this.expandedPaths.indexOf(path), 1)
                    return
                }
                if (!this.childrenCache[path]) {
                    this.$set(this.childrenCache, path, { loading: true, nodes: [] })
                    try {
                        const nodes = await vehicleDataApi.logChainExpand(this.expandParams(node))
                        this.$set(this.childrenCache, path, { loading: false, nodes: nodes || [] })
                        this.registerChildren(node)
                    } catch (_) {
                        this.$set(this.childrenCache, path, { loading: false, nodes: [] })
                    }
                }
                this.expandedPaths.push(path)
            },
            // 全链自动展开：从根按层并发调展开接口（跳过被去重隐藏的重复节点——同身份的可见节点已展开过同一子树），
            // 直到没有可展开节点；带进度提示与节点数上限（防枢纽数据把图撑爆），新查询/清空/关抽屉使进行中的展开失效（searchSeq）
            async expandAll() {
                const maxNodes = 300
                const concurrency = 4
                const token = this.searchSeq
                const queue = [{ path: '0', node: this.root }]
                let done = 0
                let truncated = false
                this.expanding = true
                this.expandProgress = 0
                try {
                    while (queue.length && token === this.searchSeq) {
                        if (done >= maxNodes) { truncated = true; break }
                        const batch = queue.splice(0, concurrency)
                        await Promise.all(batch.map(async item => {
                            const { path, node } = item
                            if (token !== this.searchSeq) return
                            const identity = this.nodeIdentity(node)
                            if (identity && this.appeared[identity] !== path) return
                            if (!node.expandable) return
                            if (this.childrenCache[path]) { this.expandedPaths.push(path); return }
                            done++
                            this.expandProgress = done
                            this.$set(this.childrenCache, path, { loading: true, nodes: [] })
                            this.expandedPaths.push(path)
                            try {
                                // 队列里存的是接口原始节点（无 path），expandParams 需 path 取祖先排除键，展开时补上
                                const nodes = await vehicleDataApi.logChainExpand(this.expandParams({ ...node, path })) || []
                                if (token !== this.searchSeq) return
                                this.$set(this.childrenCache, path, { loading: false, nodes })
                                this.registerChildren({ path })
                                nodes.forEach((child, index) => queue.push({ path: `${path}-${index}`, node: child }))
                            } catch (_) {
                                if (token !== this.searchSeq) return
                                this.$set(this.childrenCache, path, { loading: false, nodes: [] })
                            }
                        }))
                    }
                } finally {
                    if (token === this.searchSeq) this.expanding = false
                }
                if (truncated && token === this.searchSeq) this.$message.warning(`链路节点过多，已自动展开 ${done} 个，其余请逐级点开`)
            },
            // 展开参数：带节点身份（查询类型/车辆平台），并回传祖先路径上出现过的中控/车架/车辆键，
            // 后端据此做全方向反查去重（如中控反查车辆不再列出链路里已有的车辆）
            expandParams(node) {
                const params = { nodeType: node.nodeType, key: node.businessKey || node.label }
                if (node.nodeType === 'query') {
                    params.keyType = node.keyType
                } else if (node.nodeType === 'vehicle') {
                    params.platform = node.platform || undefined
                    const controls = this.ancestorKeys(node.path, 'control')
                    if (controls.length) params.excludeControls = controls.join(',')
                } else if (node.nodeType === 'control') {
                    const chassis = this.ancestorKeys(node.path, 'chassis')
                    const vehicles = this.ancestorVehicles(node.path)
                    if (chassis.length) params.excludeChassisNos = chassis.join(',')
                    if (vehicles.length) params.excludeVehicles = vehicles.join(',')
                } else if (node.nodeType === 'chassis') {
                    const controls = this.ancestorKeys(node.path, 'control')
                    if (controls.length) params.excludeControls = controls.join(',')
                }
                return params
            },
            // 祖先节点（不含自身）：按路径逐级从展开缓存里取，路径首段 0 为根
            ancestorNodes(path) {
                const segs = path.split('-')
                const result = []
                if (this.treeData && segs.length > 1) result.push(this.treeData)
                let prefix = '0'
                for (let i = 1; i < segs.length - 1; i++) {
                    const cache = this.childrenCache[prefix]
                    const node = cache && cache.nodes[Number(segs[i])]
                    if (!node) break
                    result.push(node)
                    prefix = `${prefix}-${segs[i]}`
                }
                return result
            },
            ancestorKeys(path, nodeType) {
                return this.ancestorNodes(path).filter(n => n.nodeType === nodeType).map(n => n.businessKey || n.label)
            },
            // 车辆排除键用 平台|车辆号 成对排除：另一平台的同名车辆是真数据，不误杀
            ancestorVehicles(path) {
                return this.ancestorNodes(path).filter(n => n.nodeType === 'vehicle')
                    .map(n => `${n.platform || ''}|${n.businessKey || n.label}`)
            },
            selectNode(node) {
                this.selectedPath = node.path
                this.selected = {
                    nodeType: node.nodeType,
                    businessKey: node.businessKey || node.label,
                    entityType: node.entityType,
                    // 车辆同步日志按 平台|车辆号 过滤（AuxKey2=平台），同号两平台各看各的日志
                    platform: node.platform || '',
                    typeName: nodeTypeNames[node.nodeType] || '节点'
                }
                this.loadDetail()
                this.selectedTime = []
                this.filters = { action: null, keywords: '' }
                this.page = 1
                this.loadLogs()
            },
            // 节点详情：按类型加载档案/记录信息（快速连点时以最后一次选中为准）
            async loadDetail() {
                const selected = this.selected
                this.detail = null
                this.detailLoading = true
                try {
                    const result = await vehicleDataApi.logChainDetail({
                        nodeType: selected.nodeType,
                        key: selected.businessKey,
                        platform: selected.nodeType === 'vehicle' ? selected.platform || undefined : undefined
                    })
                    if (this.selected === selected) this.detail = result
                } catch (_) {
                    if (this.selected === selected) this.detail = null
                } finally {
                    if (this.selected === selected) this.detailLoading = false
                }
            },
            formatDetail(field) {
                const value = this.detail[field.key]
                if (value == null || value === '') return '-'
                if (field.time) return this.dateTime(value)
                return field.date ? this.date(value) : String(value)
            },
            date(value) { return value ? String(value).slice(0, 10) : '-' },
            actionChanged(value) {
                this.filters.action = value === '' || value == null ? null : value
                this.searchLogs()
            },
            searchLogs() {
                this.page = 1
                this.loadLogs()
            },
            async loadLogs() {
                if (!this.selected) return
                this.loading = true
                try {
                    const [startDate, endDate] = (this.selectedTime || []).map(t => (t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : ''))
                    const result = await vehicleDataApi.logs({
                        entityType: this.selected.entityType,
                        businessKey: this.selected.businessKey,
                        auxiliaryKey2: this.selected.platform || undefined,
                        action: this.filters.action || undefined,
                        keywords: this.filters.keywords || undefined,
                        startDate: startDate || undefined,
                        endDate: endDate || undefined,
                        page: this.page,
                        size: this.size
                    })
                    this.rows = result.items || []
                    this.total = Number(result.totalCount || 0)
                } catch (_) {
                    this.rows = []
                    this.total = 0
                } finally {
                    this.loading = false
                }
            },
            dateTime(value) { return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-' },
            enumDesc(arrs, value) {
                const item = (arrs || []).find(i => i.value === value)
                return item ? item.desc : (value == null || value === '' ? '-' : value)
            },
            actionName(value) { return this.enumDesc(this.arrVehicleDataChangeAction, value) },
            sourceName(value) { return this.enumDesc(this.arrVehicleDataChangeSource, value) },
            relateKeys(row) { return [row.auxKey1, row.auxKey2].filter(x => x).join(' / ') || '-' },
            details(value) { try { return JSON.parse(value) } catch (_) { return value } },
            display(value) { return value == null ? '-' : typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value) }
        }
    }
</script>
